/**
 * Lo que tu bot sabe (GKTIBO-69).
 *
 * **Sólo lectura, y es el punto.** El dueño no es técnico y no tiene por qué
 * tocar el archivo donde se guarda su conocimiento — decisión del PO del
 * 2026-09-19. «Cambiar» no abre ninguna caja de texto: le habla al asistente,
 * que es el único que escribe.
 *
 * ## Por qué esta pantalla va primera
 *
 * Es lo único que hace **visible** si el conocimiento quedó bien. Hasta hoy,
 * para saberlo había que leer el `.md` en S3 — que es literalmente cómo se
 * encontró GKTIBO-86, mirando bytes en la consola de AWS. Mientras esto no
 * existió, cada bug del conocimiento se descubrió igual: en producción, por el
 * product owner.
 *
 * ## Y reemplaza al contador
 *
 * El formulario decía «3 de 6». Una conversación no dice nada, y no saber
 * cuánto falta es el momento exacto en que alguien abandona un alta. La lista
 * de temas, con los que faltan marcados, es lo que ocupa ese lugar.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import { PropuestaDeSeccion } from './PropuestaDeSeccion';
import { textos } from './textos';

export interface TemaDelPanel {
  id: string;
  nombre: string;
  contenido: string;
  cubierto: boolean;
  requerido: boolean;
}

interface PanelDeConocimientoProps {
  tenantId: string;
  /** Qué hacer cuando el dueño quiere cambiar un tema. Lo atiende el asistente. */
  onCambiar?: (tema: TemaDelPanel) => void;
}

/**
 * Cuánto se muestra de cada tema.
 *
 * El panel contesta **«¿qué le falta a mi bot?»**, no «¿qué dice exactamente?».
 * Lo que el rastreo trae de un sitio real son miles de caracteres —menús,
 * reseñas, notas del blog— y volcarlos enteros convierte la columna en un muro
 * que tapa los temas en falta, que es lo único accionable de la pantalla.
 *
 * El texto completo vive en el conocimiento; para cambiarlo está «Cambiar».
 */
const CUANTO_SE_MUESTRA = 200;

/** Corta en un límite de palabra, para no partir una por la mitad. */
function resumir(texto: string): string {
  if (texto.length <= CUANTO_SE_MUESTRA) return texto;
  const prefijo = texto.slice(0, CUANTO_SE_MUESTRA);
  const corte = prefijo.lastIndexOf(' ');
  return `${(corte > CUANTO_SE_MUESTRA * 0.6 ? prefijo.slice(0, corte) : prefijo).trimEnd()}…`;
}

/** El estado de un tema, que es lo que decide cómo se ve. */
function estadoDe(tema: TemaDelPanel): 'ok' | 'falta' | 'opcional' {
  if (tema.cubierto) return 'ok';
  return tema.requerido ? 'falta' : 'opcional';
}

export function PanelDeConocimiento({ tenantId, onCambiar }: PanelDeConocimientoProps) {
  const t = textos.conocimiento.panel;

  const { data, isError } = useQuery({
    queryKey: ['conocimiento-temas', tenantId],
    queryFn: () => api.tenants.temasDelConocimiento(tenantId),
  });

  const temas = data?.temas ?? [];
  const faltan = temas.filter((tema) => tema.requerido && !tema.cubierto).length;

  return (
    <aside className="panel-saber" data-testid="panel-conocimiento">
      <div className="panel-saber-head">
        <h2 className="panel-saber-titulo">{t.titulo}</h2>
        <p className="panel-saber-bajada">{t.bajada}</p>
      </div>

      {/* Un fallo de lectura NO se dibuja como «tu bot no sabe nada»: eso se
          lee como «perdiste lo que cargaste» y manda a rehacer el trabajo. */}
      {isError && <p className="panel-saber-aviso">{t.noSePudoLeer}</p>}

      {/* «Otros» es una cola: si varias cosas comparten asunto, Tibo propone
          una sección y el dueño decide (GKTIBO-147). */}
      <PropuestaDeSeccion tenantId={tenantId} />

      {!isError && temas.length === 0 && <p className="panel-saber-aviso">{t.vacio}</p>}

      {temas.length > 0 && (
        <ul className="panel-saber-temas">
          {temas.map((tema) => {
            const estado = estadoDe(tema);
            return (
              <li key={tema.id} className={`panel-tema panel-tema-${estado}`}>
                <span className="panel-tema-marca" aria-hidden="true">
                  {estado === 'ok' ? '●' : estado === 'falta' ? '▲' : '○'}
                </span>
                <div className="panel-tema-cuerpo">
                  <p className="panel-tema-nombre">{tema.nombre}</p>
                  {/* Lo que el bot sabe, en texto plano. NO se renderiza como
                      Markdown: volver a dibujar el archivo con otro estilo
                      sigue siendo mostrarle el archivo. */}
                  {tema.contenido && <p className="panel-tema-texto">{resumir(tema.contenido)}</p>}
                  <div className="panel-tema-meta">
                    {/* Un tema cubierto NO lleva etiqueta de origen. Hoy nadie
                        guarda de dónde salió cada texto —`armarMd` lo calcula
                        y lo tira—, así que decir «se lo contaste vos» sobre
                        algo que escribió el rastreo es inventar. Y al dueño le
                        salta al primer vistazo. Vuelve con GKTIBO-88, cuando
                        el servidor sea el único que escribe. */}
                    {!tema.cubierto && (
                      <span className="panel-tema-fuente">
                        {tema.requerido ? t.falta : t.opcional}
                      </span>
                    )}
                    {tema.cubierto && onCambiar && (
                      <button
                        type="button"
                        className="panel-tema-cambiar"
                        onClick={() => onCambiar(tema)}
                      >
                        {t.cambiar}
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {temas.length > 0 && (
        <p className="panel-saber-pie" data-testid="panel-conocimiento-estado">
          {faltan === 0 ? t.listo : t.cuantoFalta(faltan)}
        </p>
      )}
    </aside>
  );
}
